import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      {/* HEADER */}
      <section className="relative border-b border-white/10 bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 topo-grid opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <p className="overline">Insights</p>
            </div>
            <div className="md:col-span-8">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-[-0.03em] leading-[0.95]" data-testid="blog-title">
                Field notes from<br/>
                <span className="text-[#ff3333]">the delivery front.</span>
              </h1>
              <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
                Honest essays on AI-hybrid engineering, cybersecurity and building brands that compound. Written by the engineers and operators actually doing the work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="py-20">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <Link
              to={`/blog/${featured.slug}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 border border-white/10 hover:border-[#ff3333] transition-colors group overflow-hidden"
              data-testid={`blog-featured-${featured.id}`}
            >
              <div className="md:col-span-7 aspect-[4/3] md:aspect-auto bg-black overflow-hidden">
                <img src={featured.image_url} alt={featured.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
              </div>
              <div className="md:col-span-5 p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3">
                  <span className="overline text-[#ff3333]">{featured.category}</span>
                  <span className="text-xs mono text-zinc-600">·</span>
                  <span className="flex items-center gap-1.5 text-xs mono text-zinc-500"><Clock size={12}/> {featured.read_time}</span>
                </div>
                <h2 className="mt-4 font-display text-3xl lg:text-4xl font-black text-white tracking-tight leading-[1.1]">
                  {featured.title}
                </h2>
                <p className="mt-4 text-zinc-400 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-2 text-[#ff3333] font-semibold">
                  Read the essay <ArrowUpRight size={18}/>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* REST */}
      {rest.length > 0 && (
        <section className="pb-24">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {rest.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                data-testid={`blog-card-${p.id}`}
              >
                <Link to={`/blog/${p.slug}`} className="block group border border-white/10 hover:border-[#ff3333] transition-colors">
                  <div className="aspect-[16/9] overflow-hidden bg-black">
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3">
                      <span className="overline text-[#ff3333]">{p.category}</span>
                      <span className="flex items-center gap-1.5 text-xs mono text-zinc-500"><Clock size={12}/> {p.read_time}</span>
                    </div>
                    <h3 className="mt-3 font-display text-xl font-bold text-white tracking-tight leading-tight">{p.title}</h3>
                    <p className="mt-3 text-sm text-zinc-400 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-6">
        <p className="overline text-[#ff3333]">404</p>
        <h1 className="font-display text-4xl font-black text-white">Essay not found</h1>
        <Link to="/blog" className="btn-secondary text-sm"><ArrowLeft size={14}/> Back to Insights</Link>
      </div>
    );
  }

  if (!post) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500 mono text-sm">LOADING…</div>;

  return (
    <article className="py-20 md:py-28" data-testid="blog-detail">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-10" data-testid="blog-detail-back">
          <ArrowLeft size={14}/> All essays
        </Link>
        <p className="overline text-[#ff3333]">{post.category}</p>
        <h1 className="mt-4 font-display text-4xl md:text-5xl font-black text-white tracking-tight leading-[1.05]">{post.title}</h1>
        <div className="mt-5 flex items-center gap-4 text-sm text-zinc-500 mono">
          <span>{post.author}</span>
          <span>·</span>
          <span>{post.read_time}</span>
        </div>
        {post.image_url && (
          <div className="mt-10 aspect-[16/9] bg-black overflow-hidden border border-white/10">
            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover opacity-80" />
          </div>
        )}
        <div className="mt-10 prose prose-invert max-w-none">
          <p className="text-xl text-zinc-300 leading-relaxed font-display font-medium">{post.excerpt}</p>
          <p className="mt-8 text-zinc-400 leading-relaxed">{post.content}</p>
        </div>
      </div>
    </article>
  );
}
